import { PackageModuleDetailModel, PackageModuleModel } from "@admin/masters/pages/package-modules/models";
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ApiResources } from "@shared/helpers";
import { Counselling, CounsellingDetail, ICounsellingPatientInfo, IResource, IUserAccount, Page } from "@shared/models";
import { AppData, HttpService, NotifyService, ResourceService } from "@shared/services";
import { finalize, takeUntil } from "rxjs/operators";
import { ViewPackageModuleComponent } from "../../masters/pages/package-modules";

@Component({
    selector: "modify-counselling",
    templateUrl: "./modify-counselling.html",
    encapsulation: ViewEncapsulation.None
})
export class ModifyCounsellingComponent {
    @ViewChild(ViewPackageModuleComponent, { static: false }) viewPackageModule: ViewPackageModuleComponent;

    @Input() patientId: number;
    @Input() counsellingId: number;

    @Output() added: EventEmitter<boolean> = new EventEmitter();

    page: Page;
    loadingCounselling: boolean;
    counselling: Counselling;
    counsellingDetails: Array<CounsellingDetail>;
    loadingPatientInfo: boolean;
    patientInfo: ICounsellingPatientInfo;
    loadingLocations: boolean;
    locations: Array<IResource>;
    loadingPackageModules: boolean;
    packageModules: Array<PackageModuleModel>;
    loadingChargeCategories: boolean;
    chargeCategories: Array<IResource>;
    loadingCounsellingTypes: boolean;
    counsellingTypes: Array<IResource>;
    loadingPayTypes: boolean;
    payTypes: Array<IResource>;
    loadingInsuranceCompanies: boolean;
    insuranceCompanies: Array<IResource>;
    loadingTPAs: boolean;
    tpas: Array<IResource>;
    showPackageInfo: boolean;
    counsellingForm: FormGroup;
    isSubmitted: boolean;
    isSubmitting: boolean;
    isSavingDraft: boolean;
    signatureType: string;
    signatureFor: string;
    isSaveSignature: boolean;
    signPad: any;
    signImage: any;

    private modalRef: NgbModalRef;

    constructor(
        private readonly appData: AppData,
        private readonly modalService: NgbModal,
        private readonly httpService: HttpService,
        private readonly formBuilder: FormBuilder,
        private readonly notifyService: NotifyService,
        private readonly resourceService: ResourceService
    ) {
        this.page = new Page();
    }

    ngOnInit(): void {
        this.setUserAccount();
        this.fetchLocations();
        this.fetchCounsellingTypes();
        this.fetchPayTypes();
        this.fetchInsuranceCompanies();
        this.fetchTPAs();
    }

    get form() {
        return this.counsellingForm.controls;
    }

    onSignatureDone(event: string) {
        this.isSaveSignature = true;
        this.patchSignature(event);
        this.isSaveSignature = false;
        this.onCloseModal();
    }

    onOpenModel(content: TemplateRef<any>, signatureFor: string) {
        this.signatureType = "signature";
        this.signatureFor = signatureFor;
        this.modalRef = this.modalService.open(content, {
            backdrop: "static",
            keyboard: false,
            centered: true,
            size: "xl",
            windowClass: "custom-modal effect-scale"
        });
    }

    onReceiveDrewSignature(signature: string) {
        this.patchSignature(signature);
    }

    onSaveUploadedSignature(blob: Blob) {
        this.isSaveSignature = true;
        var reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
            const signature = reader.result as string;
            this.patchSignature(signature);
            this.isSaveSignature = false;
            this.onCloseModal();
        }
    }

    onChangeDiscount(type: string, value?: any): void {
        value = value ? parseFloat(value) : 0;
        let packageCost = parseFloat('' + this.counsellingForm.get("chargeCategory").value.total);
        switch (type) {
            case "discountType": {
                this.counsellingForm.patchValue({
                    discountPercentage: null,
                    discountAmount: null,
                    discount: 0,
                    total: packageCost
                });
                break;
            }
            case "discountPercentage": {
                let discountPercentage = value || 0;
                let discountAmount = parseFloat((((packageCost * discountPercentage) / 100) || 0).toFixed(2));
                let discount = isNaN(discountAmount) ? 0 : discountAmount;
                let total = packageCost - discount;
                this.counsellingForm.patchValue({
                    discountPercentage: discountPercentage > 0 ? discountPercentage : null,
                    discountAmount: null,
                    discount: discount,
                    total: total > 0 ? total : 0
                });
                break;
            }
            case "discountAmount": {
                let discountAmount = isNaN(value) || value <= 0 ? null : value;
                let discount = isNaN(discountAmount) ? 0 : discountAmount;
                let total = packageCost - discount;
                this.counsellingForm.patchValue({
                    discountPercentage: null,
                    discountAmount: discountAmount,
                    discount: discount,
                    total: total > 0 ? total : 0
                });
                break;
            }
        }
    }

    onCloseModal() {
        try {
            this.modalRef.close();
            this.modalRef = undefined;
        } catch (e) {
            // ignored;
        }

        this.signatureType = undefined;
        this.signatureType = undefined;
        this.isSaveSignature = undefined;
    }

    onSaveAsDraft(): void {
        this.isSavingDraft = true;
        if (this.counsellingForm.invalid) return;
        this.saveCounsellingDetails(true);
    }

    onSubmit(): void {
        this.isSubmitted = true;
        if (this.counsellingForm.invalid) return;
        this.saveCounsellingDetails();
    }

    onCancel(): void {
        this.added.emit(false);
    }

    ngOnDestroy(): void {
        this.page.unsubscribeAll();
        this.onCloseModal();
    }

    private saveCounsellingDetails(isDraft = false): void {
        const counselling = this.counsellingForm.getRawValue();
        if (!isDraft && !((counselling.patientSignature && counselling.counsellorSignature) || (counselling.patientBase64Signature && counselling.counsellorBase64Signature))) {
            this.notifyService.warning("The detailed description of the counselling must be signed by the patient and the counselor in order to be published; otherwise, save counselling as a draft.");
            return;
        }

        counselling.packageModuleId = counselling.packageModule.packageModuleId;
        counselling.chargeCategoryId = counselling.chargeCategory.id;

        delete counselling.packageModule;
        delete counselling.chargeCategory;

        if (counselling.counsellingId) counselling.modifiedBy = this.page.userAccount.accountId;
        else counselling.createdBy = this.page.userAccount.accountId;

        if (isNaN(counselling.total)) return;

        counselling.createdByRoleId = this.page.userAccount.roleId;
        counselling.createdByName = this.page.userAccount.fullName;
        counselling.umrNo = this.counselling ? this.counselling.umrNo : this.patientInfo.umrNo;
        counselling.isDraft = isDraft;

        const counsellingDetails = [];
        const packageModuleDetails = this.viewPackageModule.packageInfo.packageModuleDetails;
        packageModuleDetails.forEach((m: PackageModuleDetailModel) => {
            let counsellingDetail = new CounsellingDetail();
            counsellingDetail.packageModuleDetailId = m.packageModuleDetailId;
            counsellingDetail.amount = m.amount;
            counsellingDetail.isFree = m.isFree;
            counsellingDetail.quantity = m.quantity;
            counsellingDetails.push(counsellingDetail);
        });

        isDraft ? this.isSavingDraft = true : this.isSubmitting = true;
        this.httpService
            .post(ApiResources.getURI(ApiResources.counselling.base, ApiResources.counselling.modify), { counselling: counselling, counsellingDetails: counsellingDetails })
            .pipe(finalize(() => { this.isSubmitting = false; this.isSubmitted = false; this.isSavingDraft = false }), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (message: string) => {
                    this.notifyService.success(message);
                    this.added.emit(true);
                },
                error: () => this.notifyService.defaultError()
            });
    }

    private patchSignature(signature: string): void {
        if (this.signatureFor === "Counselling Given To")
            this.counsellingForm.get("patientBase64Signature").setValue(signature);
        else
            this.counsellingForm.get("counsellorBase64Signature").setValue(signature);
    }

    private setUserAccount(): void {
        this.appData.userAccount
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (userAccount: IUserAccount) => {
                    this.page.userAccount = userAccount;
                    !this.counsellingId && this.findPatientInfo();
                    this.counsellingId && this.findCounselling();
                },
                error: () => this.page.userAccount = undefined
            });
    }

    private fetchLocations(): void {
        this.loadingLocations = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetchLocations), {})
            .pipe(finalize(() => this.loadingLocations = false), takeUntil(this.page.unSubscribe))
            .subscribe((response: Array<IResource>) => this.locations = response);
    }

    private findCounselling(): void {
        this.loadingCounselling = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.counselling.base, ApiResources.counselling.find), { counsellingId: this.counsellingId })
            .pipe(finalize(() => this.loadingCounselling = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (response: { counselling: Counselling, counsellingDetails: Array<CounsellingDetail> }) => {
                    this.counselling = response.counselling;
                    this.counsellingDetails = response.counsellingDetails;
                    this.buildForm();
                }
            });
    }

    private findPatientInfo(): void {
        this.loadingPatientInfo = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.counselling.base, ApiResources.counselling.patientInfo), { patientId: this.patientId })
            .pipe(finalize(() => this.loadingPatientInfo = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (patientInfo: ICounsellingPatientInfo) => {
                    this.patientInfo = patientInfo;
                    this.buildForm();
                },
                error: () => this.patientInfo = undefined
            });
    }

    private fetchCounsellingTypes(): void {
        this.loadingCounsellingTypes = true;
        this.resourceService.counsellingTypes()
            .pipe(finalize(() => { this.loadingCounsellingTypes = false }))
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((counsellingTypes: Array<IResource>) => this.counsellingTypes = counsellingTypes);
    }

    private fetchPayTypes(): void {
        this.loadingPayTypes = true;
        this.resourceService.admissionPayTypes()
            .pipe(finalize(() => { this.loadingPayTypes = false }))
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((payTypes: Array<IResource>) => this.payTypes = payTypes);
    }

    private fetchInsuranceCompanies(): void {
        this.loadingInsuranceCompanies = true;
        this.resourceService.insuranceCompanies()
            .pipe(finalize(() => { this.loadingInsuranceCompanies = false }))
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((insuranceCompanies: Array<IResource>) => this.insuranceCompanies = insuranceCompanies);
    }

    private fetchTPAs(): void {
        this.loadingTPAs = true;
        this.resourceService.tpaCompanies()
            .pipe(finalize(() => { this.loadingTPAs = false }))
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((tpas: Array<IResource>) => this.tpas = tpas);
    }

    private fetchPackages(locationId: number): void {
        this.loadingPackageModules = true;
        this.httpService
            .post(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetch), { disablePagination: true, locationId })
            .pipe(finalize(() => this.loadingPackageModules = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (packageModules: Array<PackageModuleModel>) => this.packageModules = packageModules,
                error: () => this.packageModules = []
            });
    }

    private fetchChargeCategories(packageModuleId: number, chargeModuleTemplateId: number, locationId: number): void {
        this.loadingChargeCategories = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetchChargeCategoriesWithTotal), { packageModuleId, chargeModuleTemplateId, locationId })
            .pipe(finalize(() => this.loadingChargeCategories = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (chargeCategories: Array<IResource>) => {
                    const packageModule = this.counsellingForm.get("packageModule").value as PackageModuleModel;
                    if (chargeCategories.length && packageModule) {
                        chargeCategories.forEach((chargeCategory: IResource) => {
                            let subTotal = chargeCategory.value ? parseFloat(chargeCategory.value) : 0, discount = 0, total = subTotal;
                            if (packageModule.discountType === "P") {
                                discount = parseFloat((((subTotal * (packageModule.discountPercentage || 0)) / 100) || 0).toFixed(2));
                                discount = isNaN(discount) ? 0 : discount;
                                total = subTotal - discount;
                                total = total > 0 ? total : 0;
                            } else if (packageModule.discountType === "A") {
                                discount = packageModule.discountAmount || 0;
                                discount = isNaN(discount) ? 0 : discount;
                                total = subTotal - discount;
                                total = total > 0 ? total : 0;
                            }

                            chargeCategory.subTotal = subTotal;
                            chargeCategory.discount = discount;
                            chargeCategory.total = total;
                        });
                    }

                    this.chargeCategories = chargeCategories;
                },
                error: () => this.chargeCategories = []
            });
    }

    private buildForm(): void {
        let locationId = this.page.userAccount.locationId;
        let packageModule: PackageModuleModel | null = null;
        if (this.counselling && this.counselling.packageModuleId !== 0) {
            packageModule = new PackageModuleModel();
            packageModule.packageModuleId = this.counselling.packageModuleId;
            packageModule.packageName = this.counselling.packageName;
            packageModule.packageTypeName = this.counselling.packageTypeName;
            packageModule.moduleTypeName = this.counselling.moduleTypeName;
            packageModule.chargeModuleTemplateId = this.counselling.chargeModuleTemplateId;
            packageModule.templateName = this.counselling.templateName;
            packageModule.startDate = this.counselling.startDate;
            packageModule.endDate = this.counselling.endDate;
            packageModule.locationId = this.counselling.locationId;
            locationId = this.counselling.locationId;
        }

        let chargeCategory: IResource | null = null;
        if (this.counselling && this.counselling.chargeCategoryId !== 0)
            chargeCategory = { id: this.counselling.chargeCategoryId, name: this.counselling.chargeCategoryName } as IResource;

        this.counsellingForm = this.formBuilder.group({
            counsellingId: this.counselling && this.counselling.counsellingId || 0,
            patientId: this.counselling && this.counselling.patientId || this.patientId,
            locationId: [locationId, [Validators.required]],
            packageModule: [packageModule, [Validators.required]],
            chargeCategory: [chargeCategory, [Validators.required]],
            counsellingTypeId: [this.counselling && this.counselling.counsellingTypeId, [Validators.required]],
            totalQuantity: this.counselling && this.counselling.totalQuantity || 0,
            subTotal: this.counselling && this.counselling.subTotal || null,
            packageDiscount: this.counselling && this.counselling.packageDiscount || null,
            discountType: this.counselling && this.counselling.discountType || "P",
            discountPercentage: this.counselling && this.counselling.discountPercentage || null,
            discountAmount: this.counselling && this.counselling.discountAmount || null,
            discount: this.counselling && this.counselling.discount || 0,
            total: this.counselling && this.counselling.total || null,
            payTypeId: this.counselling && this.counselling.payTypeId || null,
            insuranceCompanyId: this.counselling && this.counselling.insuranceCompanyId || null,
            tpaId: this.counselling && this.counselling.tpaId || null,
            patientOrganization: this.counselling && this.counselling.patientOrganization || null,
            patientBase64Signature: null,
            counsellorBase64Signature: null,
            patientSignature: this.counselling && this.counselling.patientSignature || null,
            counsellorSignature: this.counselling && this.counselling.counsellorSignature || null,
            comments: this.counselling && this.counselling.comments || null
        });

        if (locationId) this.fetchPackages(locationId);

        if (this.counselling) {
            this.showPackageInfo = true;
            this.fetchChargeCategories(packageModule.packageModuleId, packageModule.chargeModuleTemplateId, packageModule.locationId);
        }

        this.counsellingForm.get("locationId").valueChanges
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((locationId?: number) => {
                this.loadingPackageModules = false;
                this.packageModules = [];
                this.counsellingForm.get("packageModule").setValue(null);
                locationId && this.fetchPackages(locationId);
            });

        this.counsellingForm.get("packageModule").valueChanges
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((packageModule?: PackageModuleModel) => {
                this.showPackageInfo = false;
                this.chargeCategories = [];
                this.counsellingForm.get("chargeCategory").setValue(null);
                packageModule && packageModule.packageModuleId && this.fetchChargeCategories(packageModule.packageModuleId, packageModule.chargeModuleTemplateId, packageModule.locationId);
                this.counsellingForm.get("totalQuantity").setValue(packageModule && packageModule.quantity || 0);
            });

        this.counsellingForm.get("chargeCategory").valueChanges
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((chargeCategory?: IResource) => {
                this.showPackageInfo = chargeCategory ? true : false;
                this.counsellingForm.patchValue({
                    subTotal: chargeCategory && chargeCategory.subTotal || null,
                    packageDiscount: chargeCategory && chargeCategory.discount || null,
                    discountType: "P",
                    discountPercentage: null,
                    discountAmount: null,
                    discount: 0,
                    total: chargeCategory && chargeCategory.total || null
                });
            });
    }
}